import { Container, Sprite, Assets, Texture } from 'pixi.js';

// ── Expression types ───────────────────────────────────────────────────────────
export type EyeExpression = 'happy' | 'surprised' | 'nervous' | 'determined' | 'dizzy';
export type MouthExpression = 'happy' | 'open' | 'determined';

// ── Cosmetics interface ────────────────────────────────────────────────────────
export interface AvatarCosmetics {
  hat?: string | null;     // e.g. 'hat-crown', 'hat-beanie', 'hat-halo'
  outfit?: string | null;  // e.g. 'outfit-shirt'
  weapon?: string | null;  // e.g. 'weapon-spatula'
}

export type CosmeticSlot = keyof AvatarCosmetics;

// ── Avatar ─────────────────────────────────────────────────────────────────────

/**
 * Avatar is a Pixi Container that composites named SVG layers.
 *
 * All SVG assets share viewBox="0 0 80 140".
 * Layer order (bottom → top): body, eyes, mouth, outfit, hat, weapon.
 *
 * Usage:
 *   const avatar = new Avatar({ hat: 'hat-crown' });
 *   await avatar.load();
 *   app.stage.addChild(avatar);
 *   avatar.position.set(400, 300);
 *   avatar.playIdleAnimation();
 */
export class Avatar extends Container {
  // ── Static texture cache shared across all Avatar instances ──────────────────
  private static textureCache = new Map<string, Texture>();

  // ── Per-instance state ───────────────────────────────────────────────────────
  private cosmetics: AvatarCosmetics;

  // Current loaded textures
  private bodyTexture: Texture | null = null;
  private eyeTexture: Texture | null = null;
  private mouthTexture: Texture | null = null;
  private outfitTexture: Texture | null = null;
  private hatTexture: Texture | null = null;
  private weaponTexture: Texture | null = null;

  // Current expression keys
  private currentEyes: EyeExpression = 'happy';
  private currentMouth: MouthExpression = 'happy';

  // Animation state
  private animationId: number | null = null;
  private baseY: number = 0;
  private animStart: number | null = null;

  constructor(cosmetics: AvatarCosmetics = {}) {
    super();
    this.cosmetics = { hat: null, outfit: null, weapon: null, ...cosmetics };
    // Pivot at centre of the 80×140 coordinate space
    this.pivot.set(40, 70);
  }

  // ── Texture loading ──────────────────────────────────────────────────────────

  /**
   * Load a texture by key, using the shared cache to avoid duplicate requests.
   * Key maps to `/assets/avatars/<key>.svg`.
   */
  private async loadTexture(key: string): Promise<Texture> {
    const cached = Avatar.textureCache.get(key);
    if (cached) return cached;

    const url = '/assets/avatars/' + key + '.svg';
    const texture = await Assets.load<Texture>(url);
    Avatar.textureCache.set(key, texture);
    return texture;
  }

  // ── Public API ───────────────────────────────────────────────────────────────

  /**
   * Load all required textures and build the initial layer stack.
   * Must be called (and awaited) before adding the avatar to the stage.
   */
  async load(): Promise<void> {
    // Always load base layers
    this.bodyTexture  = await this.loadTexture('body');
    this.eyeTexture   = await this.loadTexture('eyes-happy');
    this.mouthTexture = await this.loadTexture('mouth-happy');

    // Load cosmetic layers only if set
    if (this.cosmetics.outfit) {
      this.outfitTexture = await this.loadTexture(this.cosmetics.outfit);
    }
    if (this.cosmetics.hat) {
      this.hatTexture = await this.loadTexture(this.cosmetics.hat);
    }
    if (this.cosmetics.weapon) {
      this.weaponTexture = await this.loadTexture(this.cosmetics.weapon);
    }

    this.buildLayers();
  }

  /**
   * Swap the eye and mouth expression layers.
   * Textures are fetched from cache or loaded on demand.
   */
  async setExpression(eyes: EyeExpression, mouth: MouthExpression): Promise<void> {
    this.currentEyes  = eyes;
    this.currentMouth = mouth;

    this.eyeTexture   = await this.loadTexture('eyes-' + eyes);
    this.mouthTexture = await this.loadTexture('mouth-' + mouth);

    this.buildLayers();
  }

  /**
   * Add, swap, or remove a cosmetic layer.
   * Pass null to remove the cosmetic from that slot.
   */
  async updateCosmetic(slot: CosmeticSlot, key: string | null): Promise<void> {
    this.cosmetics[slot] = key;

    if (key) {
      const texture = await this.loadTexture(key);
      switch (slot) {
        case 'hat':    this.hatTexture    = texture; break;
        case 'outfit': this.outfitTexture = texture; break;
        case 'weapon': this.weaponTexture = texture; break;
      }
    } else {
      switch (slot) {
        case 'hat':    this.hatTexture    = null; break;
        case 'outfit': this.outfitTexture = null; break;
        case 'weapon': this.weaponTexture = null; break;
      }
    }

    this.buildLayers();
  }

  /**
   * Start a gentle idle bob animation (±3 px, 1.5 s cycle).
   * Stores a rAF id; call stopIdleAnimation() to cancel.
   */
  playIdleAnimation(): void {
    this.stopIdleAnimation();

    this.baseY    = this.position.y;
    this.animStart = null;

    const BOB_AMPLITUDE = 3;   // pixels
    const BOB_PERIOD    = 1500; // milliseconds

    const tick = (timestamp: number) => {
      if (this.animStart === null) this.animStart = timestamp;
      const elapsed = timestamp - this.animStart;
      this.position.y = this.baseY + BOB_AMPLITUDE * Math.sin((elapsed / BOB_PERIOD) * Math.PI * 2);
      this.animationId = requestAnimationFrame(tick);
    };

    this.animationId = requestAnimationFrame(tick);
  }

  /** Cancel the idle bob animation. */
  stopIdleAnimation(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  /**
   * Rebuild all child sprites from current texture state.
   * Called after load(), setExpression(), and updateCosmetic().
   *
   * Layer order (bottom → top):
   *   body → eyes → mouth → outfit → hat → weapon
   */
  private buildLayers(): void {
    // Remove all existing children
    this.removeChildren();

    const addLayer = (texture: Texture | null): void => {
      if (!texture) return;
      const sprite = Sprite.from(texture);
      // All SVGs share the same 80×140 coordinate space — position at origin
      sprite.position.set(0, 0);
      this.addChild(sprite);
    };

    // Bottom-to-top render order
    addLayer(this.bodyTexture);
    addLayer(this.eyeTexture);
    addLayer(this.mouthTexture);
    addLayer(this.outfitTexture);
    addLayer(this.hatTexture);
    addLayer(this.weaponTexture);
  }
}
